import { useState, useMemo, useCallback, useRef } from "react";
import { DEFAULT_BUFFER, DEFAULT_CARD_GAP, DEFAULT_CARD_WIDTH, DEFAULT_AMOUNT_OF_CARDS_IN_WINDOW } from "../constants/virtualization";

export const useVirtualization = ({ items, cardWidth = DEFAULT_CARD_WIDTH, cardGap = DEFAULT_CARD_GAP, amountOfCardsInWindow = DEFAULT_AMOUNT_OF_CARDS_IN_WINDOW, buffer = DEFAULT_BUFFER }) => {
    const scrollingRef = useRef(null);
    // Index for the elements we are going to slice
    const [visibleWindow, setVisibleWindow] = useState({
        start: 0,
        end: Math.min(amountOfCardsInWindow, items.length),
    });

    const cardWidthWithGap = cardWidth + cardGap; // Total card space + right gap (24 as for now)

    const onItemFocus = useCallback(
        ({ x, index: focusedGlobalIndex }) => {
            if (scrollingRef.current) {
                scrollingRef.current.scrollLeft = x;
            }

            if (items.length <= amountOfCardsInWindow) {
                return;
            }

            let newStart = visibleWindow.start;
            if (focusedGlobalIndex >= visibleWindow.end - buffer) {
                // Move the items window to the right (scroll)
                const maxStartIndex = Math.max(0, items.length - amountOfCardsInWindow);
                newStart = Math.min(maxStartIndex, focusedGlobalIndex - buffer + 1);
            } else if (focusedGlobalIndex < visibleWindow.start + buffer) {
                // Move the items window to the left (scroll)
                newStart = Math.max(0, focusedGlobalIndex - buffer - 1);
            }
            const newEnd = Math.min(newStart + amountOfCardsInWindow, items.length);
            if (newStart !== visibleWindow.start) {
                // Don't move if we are not in the edges
                setVisibleWindow({ start: newStart, end: newEnd });
            }
        },
        [visibleWindow, items.length, amountOfCardsInWindow, buffer, scrollingRef]
    );

    // Reserves enough space to render all the cards (but we are not going to use it all at once)
    // Updates the padding to create a 'scrolling' effect
    const contentStyle = useMemo(() => {
        const totalWidth = items.length * cardWidthWithGap - cardGap;
        const paddingLeft = visibleWindow.start * cardWidthWithGap;
        return {
            width: totalWidth > 0 ? `${totalWidth}px` : "0px",
            paddingLeft: `${paddingLeft}px`,
        };
    }, [visibleWindow.start, items.length, cardWidthWithGap, cardGap]);

    // Only cards to render are sliced here
    const virtualItems = useMemo(
        () =>
            items.slice(visibleWindow.start, visibleWindow.end).map((item, index) => ({
                item,
                index: visibleWindow.start + index, // Global index
            })),
        [items, visibleWindow]
    );


    return {
        scrollingRef,
        contentStyle,
        virtualItems,
        onItemFocus,
    };
};
